import {BACKEND_URL} from "../../../../configs";
import {globalFetchConfig} from "../../../../api/apiConfiguration";
import {DownloadedFile, readErrorMessage, readFileName, saveDownloadedFile} from "../../../../utils/fileTransferUtils";

/**
 * Export and import of an assistant's configuration.
 *
 * Hand written rather than generated, because both endpoints move a ZIP archive rather than JSON
 * and the generated client models every body as JSON. Auth still goes through globalFetchConfig,
 * so these calls carry the same Clerk token as every other request.
 */

export interface AssistantImportResult {
    assistantPublicId: string;
    assistantName: string;
    franchisesCreated: number;
    franchisesUpdated: number;
    locationsCreated: number;
    locationsUpdated: number;
    offersRestored: number;
    offersSkipped: number;
}

export type EmailCloneStrategy = "KEEP" | "PLACEHOLDER";

export interface CloneAssistantRequest {
    namePrefix: string;
    copyFranchisesAndLocations: boolean;
    emailStrategy?: EmailCloneStrategy;
    placeholderEmail?: string;
}

export interface AssistantCloneResult {
    assistantPublicId: string;
    assistantName: string;
    franchisesCopied: number;
    locationsCopied: number;
    offersCopied: number;
    vehicleRowsCopied: number;
    /** True when the assistant was created but its vehicle stock could not be duplicated. */
    vehicleCloneFailed: boolean;
}

export type AssistantExportFile = DownloadedFile;

const FALLBACK_FILE_NAME = "assistant-configuration.zip";

export const exportAssistantConfiguration = async (publicBotId: string): Promise<AssistantExportFile> => {
    const response = await globalFetchConfig(
        `${BACKEND_URL}/api/private/bot/${encodeURIComponent(publicBotId)}/export`,
        {method: "GET"}
    );

    if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Failed to export the assistant configuration"));
    }

    return {
        fileName: readFileName(response.headers.get("Content-Disposition"), FALLBACK_FILE_NAME),
        blob: await response.blob()
    };
};

export const importAssistantConfiguration = async (publicBotId: string,
                                                   file: File): Promise<AssistantImportResult> => {
    const response = await globalFetchConfig(
        `${BACKEND_URL}/api/private/bot/${encodeURIComponent(publicBotId)}/import`,
        {
            method: "POST",
            // Sent as the raw archive. The backend reads the body directly, so there is no
            // multipart boundary for the browser to negotiate.
            headers: {"Content-Type": "application/zip"},
            body: file
        }
    );

    if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Failed to import the assistant configuration"));
    }

    return await response.json();
};

export const cloneAssistantConfiguration = async (publicBotId: string,
                                                 request: CloneAssistantRequest): Promise<AssistantCloneResult> => {
    const response = await globalFetchConfig(
        `${BACKEND_URL}/api/private/bot/${encodeURIComponent(publicBotId)}/clone`,
        {method: "POST", body: JSON.stringify(request)}
    );

    if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Failed to clone the assistant"));
    }

    return await response.json();
};

export const saveExportedFile = saveDownloadedFile;
